import axios from "axios";
import { getRestApiUrl } from "../backendOverride";
import { getActiveSedeId, getPartnerSessionId } from "./partnerStorage";

// ---------------------------------------------------------------------------
// Client for the partner access surface (`/partner/v1`).
//
// WHY ITS OWN INSTANCE AND NOT THE SHARED `API` (src/axios.js):
//  - `/partner/v1` only accepts the NORMAL JWT. The shared client is the one
//    that will carry the effective (delegated) token while a sede is active,
//    and a delegated token is rejected here with a 401.
//  - The shared response interceptor logs the user out on `UNAUTHORIZED`
//    (it wipes localStorage and redirects). A partner-access failure must
//    never end the normal session, so this instance has NO response
//    interceptor.
//
// Same precedent as `utils/providerIncidents.js`. As there, `sentry-trace` and
// `baggage` are injected by Sentry through `tracePropagationTargets`
// (observability/init.js), which matches on the request URL and does not
// depend on the axios instance: this client resolves its base URL with the
// same `getRestApiUrl()` as the shared one, so it is covered.
// ---------------------------------------------------------------------------

const PARTNER_BASE_PATH = "/partner/v1";
const PARTNER_SESSION_HEADER = "X-Partner-Session";

// `resolveEffectiveTokenAtBoot()` awaits this client before the first render:
// without a bound, a backend that never answers would leave a blank page.
const REQUEST_TIMEOUT_MS = 10000;

export const PARTNER_ERROR_CODES = {
  UNAUTHENTICATED: "UNAUTHENTICATED",
  NOT_A_PARTNER: "NOT_A_PARTNER",
  SEDE_NOT_IN_GROUP: "SEDE_NOT_IN_GROUP",
  SESSION_EXPIRED: "SESSION_EXPIRED",
  FEATURE_DISABLED: "FEATURE_DISABLED",
  RATE_LIMITED: "RATE_LIMITED",
  UNAVAILABLE: "UNAVAILABLE",
  // Client-side only: the request got no response at all.
  NETWORK: "NETWORK",
  // Client-side only: a sede-scoped call was made from a tab that works none.
  NO_ACTIVE_SEDE: "NO_ACTIVE_SEDE",
};

/**
 * The single failure shape of this client. It deliberately carries nothing
 * from the request (headers hold the JWT and the partner session id) and no
 * backend text, so it is safe to hand to Sentry as is.
 */
export class PartnerAccessError extends Error {
  constructor({ code, httpStatus, retryAfterSeconds } = {}) {
    super(`Partner access request failed: ${code}`);
    this.name = "PartnerAccessError";
    this.code = code;
    this.httpStatus = httpStatus;
    if (retryAfterSeconds !== undefined) this.retryAfterSeconds = retryAfterSeconds;
  }
}

const partnerApi = axios.create({ timeout: REQUEST_TIMEOUT_MS });

partnerApi.interceptors.request.use((config) => {
  // A call may name the JWT it acts for (see `revokePartnerSession`); every
  // other request authenticates as whoever is signed in right now.
  if (!config.headers.Authorization) {
    // Nobody signed in (for example after a sign-out in another tab): the
    // request goes out without the header rather than as `Bearer null`.
    const normalToken = localStorage.getItem("token");
    if (normalToken) config.headers.Authorization = `Bearer ${normalToken}`;
  }
  config.baseURL = `${getRestApiUrl()}`;
  return config;
});
// On purpose: NO response interceptor. See the note above.

// Only the delta-seconds form of `Retry-After` is supported; an HTTP date
// would need the server clock, which this feature avoids relying on. Anything
// that is not a plain number is "absent": `Number("  ")` would read as 0.
const DELTA_SECONDS_PATTERN = /^\d+(\.\d+)?$/;

const parseRetryAfterSeconds = (headers) => {
  const raw = headers?.["retry-after"];
  if (typeof raw !== "string" && typeof raw !== "number") return undefined;
  const text = String(raw).trim();
  return DELTA_SECONDS_PATTERN.test(text) ? Number(text) : undefined;
};

const FALLBACK_CODE_BY_STATUS = {
  401: PARTNER_ERROR_CODES.UNAUTHENTICATED,
  404: PARTNER_ERROR_CODES.FEATURE_DISABLED,
};

const envelopeCode = (data) => {
  const code = data?.error?.code;
  return typeof code === "string" && code !== "" ? code : null;
};

const toPartnerAccessError = (error) => {
  const response = error?.response;
  if (!response) {
    return new PartnerAccessError({ code: PARTNER_ERROR_CODES.NETWORK });
  }

  const httpStatus = response.status;
  // Without the envelope the answer did not come from the partner routes: a
  // 401 is the normal session being rejected before reaching them, a 404 is a
  // backend that does not have them yet, anything else is an outage.
  const fallbackCode =
    FALLBACK_CODE_BY_STATUS[httpStatus] || PARTNER_ERROR_CODES.UNAVAILABLE;

  return new PartnerAccessError({
    code: envelopeCode(response.data) || fallbackCode,
    httpStatus,
    retryAfterSeconds:
      httpStatus === 429 ? parseRetryAfterSeconds(response.headers) : undefined,
  });
};

const request = async (config) => {
  try {
    const { data } = await partnerApi.request(config);
    return data;
  } catch (error) {
    throw toPartnerAccessError(error);
  }
};

const withPartnerSession = (sessionId) => ({ [PARTNER_SESSION_HEADER]: sessionId });

/** → `{ sessionId, expiresAt, group, sedes }`. The session lasts 12 h. */
export const createPartnerSession = () =>
  request({ method: "post", url: `${PARTNER_BASE_PATH}/session` });

const asBearer = (normalToken) =>
  typeof normalToken === "string" && normalToken !== ""
    ? { Authorization: `Bearer ${normalToken}` }
    : {};

/**
 * → `{ revoked: true }`. A partner session can only be revoked by the normal
 * session that opened it: `normalToken` names that JWT when it is no longer
 * the one in storage (the user signed out, or another one signed in, while
 * the session was being opened — see effectiveToken.js). Without it the
 * stored JWT is used, as in every other call.
 */
export const revokePartnerSession = (sessionId, { normalToken } = {}) =>
  request({
    method: "delete",
    url: `${PARTNER_BASE_PATH}/session`,
    headers: { ...withPartnerSession(sessionId), ...asBearer(normalToken) },
  });

/** → `{ token, companyId, expiresAt, expiresInSeconds }`. */
export const mintSedeToken = ({ sessionId, companyId }) =>
  request({
    method: "post",
    url: `${PARTNER_BASE_PATH}/sedes/${encodeURIComponent(companyId)}/token`,
    headers: withPartnerSession(sessionId),
  });

/** → waiting/late counters per sede. Do not poll faster than every 15 s. */
export const getGroupSummary = (sessionId) =>
  request({
    method: "get",
    url: `${PARTNER_BASE_PATH}/group/summary`,
    headers: withPartnerSession(sessionId),
  });

/**
 * A call scoped to the sede this tab works: `path` is what follows
 * `/partner/v1/sedes/:companyId` (for example `/boards`). Like every call of
 * this client it carries the NORMAL JWT plus the partner session, never the
 * delegated token. The sede and the session are read on every call: a token
 * mint may have renewed the session meanwhile.
 *
 * Nothing is sent when the tab works no sede (`NO_ACTIVE_SEDE`) or holds no
 * partner session (`SESSION_EXPIRED`, what the backend answers to a request
 * without the header). `timeout` overrides the 10 s default of the client.
 */
export const sedeRequest = async ({ method = "get", path = "", data, timeout } = {}) => {
  const companyId = getActiveSedeId();
  if (companyId === null) {
    throw new PartnerAccessError({ code: PARTNER_ERROR_CODES.NO_ACTIVE_SEDE });
  }
  const sessionId = getPartnerSessionId();
  if (sessionId === null) {
    throw new PartnerAccessError({ code: PARTNER_ERROR_CODES.SESSION_EXPIRED });
  }

  return request({
    method,
    url: `${PARTNER_BASE_PATH}/sedes/${encodeURIComponent(companyId)}${path}`,
    headers: withPartnerSession(sessionId),
    ...(data !== undefined && { data }),
    ...(timeout !== undefined && { timeout }),
  });
};
